import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  ArrowLeft, Upload, Trash2, Edit2, Save, X, Image,
  Package, Eye, EyeOff, GripVertical, LogOut, ShoppingCart, BookOpen, Loader2,
  Store, Search, Plus, CheckCircle2
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function AdminDashboard() {
  const navigate = useNavigate();
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [password, setPassword] = useState('');
  const [token, setToken] = useState('');
  const [loading, setLoading] = useState(false);

  const [banners, setBanners] = useState([]);
  const [orders, setOrders] = useState([]);
  const [activeTab, setActiveTab] = useState('banners');
  const [editImageFile, setEditImageFile] = useState(null); // ✅ New state

  // Banner upload form
  const [uploadForm, setUploadForm] = useState({
    title: '',
    link_url: '',
    order: 0,
    file: null
  });
  const [uploading, setUploading] = useState(false);
  const [editingBanner, setEditingBanner] = useState(null);

  // Tutorial Seller state
  const [tutorialSteps, setTutorialSteps] = useState([]);
  const [editingStep, setEditingStep] = useState(null);
  const [stepImageFile, setStepImageFile] = useState(null);
  const [updatingStep, setUpdatingStep] = useState(false);
  const [initializingTutorial, setInitializingTutorial] = useState(false);

  // Whitelist Merchant state
  const [whitelist, setWhitelist] = useState([]);
  const [wlForm, setWlForm] = useState({ alias: '', store_id: '', lon: '', lat: '' });
  const [wlVerifying, setWlVerifying] = useState(false);
  const [wlAdding, setWlAdding] = useState(false);
  const [wlVerified, setWlVerified] = useState(null); // hasil verify dari MPX

  // Check for existing token
  useEffect(() => {
    const savedToken = localStorage.getItem('admin_token');
    if (savedToken) {
      setToken(savedToken);
      setIsAuthenticated(true);
    }
  }, []);

  // Fetch data when authenticated
  useEffect(() => {
    if (isAuthenticated) {
      fetchBanners();
      fetchOrders();
      fetchTutorialSteps();
      fetchWhitelist();
    }
    // Fetch helpers are stable for this screen; only re-run when auth changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated]);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const response = await axios.post(`${API}/admin/login?password=${password}`);
      if (response.data.success) {
        setToken(response.data.token);
        localStorage.setItem('admin_token', response.data.token);
        setIsAuthenticated(true);
        toast.success('Login berhasil!');
      }
    } catch (error) {
      toast.error('Password salah!');
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('admin_token');
    setToken('');
    setIsAuthenticated(false);
    toast.success('Logout berhasil');
  };

  const fetchBanners = async () => {
    try {
      const response = await axios.get(`${API}/banners?active_only=false`);
      setBanners(response.data.banners || []);
    } catch (error) {
      console.error('Error fetching banners:', error);
    }
  };

  const fetchOrders = async () => {
    try {
      const response = await axios.get(`${API}/admin/orders`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setOrders(response.data.orders || []);
    } catch (error) {
      console.error('Error fetching orders:', error);
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setUploadForm(prev => ({ ...prev, file }));
    }
  };

  const handleUploadBanner = async (e) => {
    e.preventDefault();

    // Validation
    if (!uploadForm.file) {
      toast.error('Pilih gambar terlebih dahulu');
      return;
    }

    // if (!uploadForm.title.trim()) {
    //   toast.error('Masukkan judul banner');
    //   return;
    // }

    setUploading(true);

    try {
      // Create FormData with all fields
      const formData = new FormData();
      formData.append('file', uploadForm.file);
      formData.append('title', uploadForm.title);
      formData.append('link_url', uploadForm.link_url || '');
      formData.append('order', uploadForm.order.toString());

      console.log('Uploading banner:', {
        title: uploadForm.title,
        link_url: uploadForm.link_url,
        order: uploadForm.order,
        fileName: uploadForm.file.name
      });

      // Send request
      const response = await axios.post(`${API}/admin/banners`, formData, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });

      console.log('Upload success:', response.data);
      toast.success('Banner berhasil diupload!');

      // Reset form
      setUploadForm({
        title: '',
        link_url: '',
        order: 0,
        file: null
      });

      // Clear file input manually
      const fileInput = document.getElementById('file');
      if (fileInput) {
        fileInput.value = '';
      }

      // Refresh banners list
      fetchBanners();

    } catch (error) {
      console.error('Upload error:', error);

      // ✅ Better error handling - convert object to string
      let errorMessage = 'Gagal upload banner';

      if (error.response?.data) {
        const errorData = error.response.data;

        // Handle FastAPI validation errors
        if (errorData.detail) {
          if (Array.isArray(errorData.detail)) {
            // Validation errors array
            errorMessage = errorData.detail
              .map(err => `${err.loc?.join(' → ') || 'Error'}: ${err.msg}`)
              .join(', ');
          } else if (typeof errorData.detail === 'string') {
            // Simple string error
            errorMessage = errorData.detail;
          } else {
            // Object error
            errorMessage = JSON.stringify(errorData.detail);
          }
        } else if (errorData.message) {
          errorMessage = errorData.message;
        }
      } else if (error.message) {
        errorMessage = error.message;
      }

      console.error('Error details:', errorMessage);
      toast.error(errorMessage);
    } finally {
      setUploading(false);
    }
  };
  // const handleUploadBanner = async (e) => {
  //   e.preventDefault();
  //   if (!uploadForm.file || !uploadForm.title) {
  //     toast.error('Pilih gambar dan masukkan judul');
  //     return;
  //   }

  //   setUploading(true);
  //   try {
  //     const formData = new FormData();
  //     formData.append('file', uploadForm.file);

  //     const params = new URLSearchParams({
  //       title: uploadForm.title,
  //       link_url: uploadForm.link_url || '',
  //       order: uploadForm.order.toString()
  //     });

  //     await axios.post(`${API}/admin/banners?${params}`, formData, {
  //       headers: {
  //         'Authorization': `Bearer ${token}`,
  //         'Content-Type': 'multipart/form-data'
  //       }
  //     });

  //     toast.success('Banner berhasil diupload!');
  //     setUploadForm({ title: '', link_url: '', order: 0, file: null });
  //     fetchBanners();
  //   } catch (error) {
  //     console.error('Upload error:', error);
  //     toast.error('Gagal upload banner');
  //   } finally {
  //     setUploading(false);
  //   }
  // };

  const handleDeleteBanner = async (bannerId) => {
    if (!confirm('Yakin ingin menghapus banner ini?')) return;

    try {
      await axios.delete(`${API}/admin/banners/${bannerId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Banner berhasil dihapus');
      fetchBanners();
    } catch (error) {
      toast.error('Gagal menghapus banner');
    }
  };

  const handleToggleBanner = async (banner) => {
    try {
      await axios.put(`${API}/admin/banners/${banner.id}`,
        { is_active: !banner.is_active },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      fetchBanners();
      toast.success(`Banner ${!banner.is_active ? 'diaktifkan' : 'dinonaktifkan'}`);
    } catch (error) {
      toast.error('Gagal update banner');
    }
  };

  const handleUpdateBanner = async (banner) => {
    try {
      const formData = new FormData();

      // Add fields that changed
      if (editingBanner.title !== banner.title) {
        formData.append('title', editingBanner.title);
      }

      if (editingBanner.link_url !== banner.link_url) {
        formData.append('link_url', editingBanner.link_url || '');
      }

      if (editingBanner.order !== banner.order) {
        formData.append('order', editingBanner.order.toString());
      }

      // Add new image if selected
      if (editImageFile) {
        formData.append('file', editImageFile);
      }

      await axios.put(`${API}/admin/banners/${banner.id}`, formData, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });

      toast.success('Banner berhasil diupdate');
      setEditingBanner(null);
      setEditImageFile(null);
      fetchBanners();
    } catch (error) {
      console.error('Update error:', error);
      toast.error('Gagal update banner');
    }
  };

  // const handleUpdateBanner = async (banner) => {
  //   try {
  //     await axios.put(`${API}/admin/banners/${banner.id}`,
  //       {
  //         title: editingBanner.title,
  //         link_url: editingBanner.link_url,
  //         order: editingBanner.order
  //       },
  //       { headers: { Authorization: `Bearer ${token}` } }
  //     );
  //     toast.success('Banner berhasil diupdate');
  //     setEditingBanner(null);
  //     fetchBanners();
  //   } catch (error) {
  //     toast.error('Gagal update banner');
  //   }
  // };

  const formatPrice = (price) => {
    return new Intl.NumberFormat('id-ID').format(price);
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('id-ID', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // ========== Tutorial Seller Functions ==========
  const fetchTutorialSteps = async () => {
    try {
      const response = await axios.get(`${API}/admin/seller/tutorial-steps`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTutorialSteps(response.data.steps || []);
    } catch (error) {
      console.error('Error fetching tutorial steps:', error);
      // Try public endpoint as fallback
      try {
        const pubResponse = await axios.get(`${API}/seller/tutorial-steps`);
        setTutorialSteps(pubResponse.data.steps || []);
      } catch (e) {
        console.error('Fallback also failed:', e);
      }
    }
  };

  const handleInitTutorial = async () => {
    setInitializingTutorial(true);
    try {
      await axios.post(`${API}/admin/seller/init-tutorial`, null, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Tutorial steps berhasil diinisialisasi!');
      fetchTutorialSteps();
    } catch (error) {
      console.error('Init tutorial error:', error);
      toast.error('Gagal inisialisasi tutorial');
    } finally {
      setInitializingTutorial(false);
    }
  };

  const handleUpdateStep = async (step) => {
    setUpdatingStep(true);
    try {
      const formData = new FormData();

      if (editingStep.title !== step.title) {
        formData.append('title', editingStep.title);
      }
      if (editingStep.description !== step.description) {
        formData.append('description', editingStep.description);
      }
      if (editingStep.detail_instructions !== step.detail_instructions) {
        formData.append('detail_instructions', editingStep.detail_instructions);
      }
      if (stepImageFile) {
        formData.append('file', stepImageFile);
      }

      await axios.put(`${API}/admin/seller/tutorial/${step.id}`, formData, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });

      toast.success('Tutorial step berhasil diupdate!');
      setEditingStep(null);
      setStepImageFile(null);
      fetchTutorialSteps();
    } catch (error) {
      console.error('Update step error:', error);
      toast.error('Gagal update tutorial step');
    } finally {
      setUpdatingStep(false);
    }
  };

  // ========== Whitelist Merchant Functions ==========
  const authHeader = { headers: { Authorization: `Bearer ${token}` } };

  const fetchWhitelist = async () => {
    try {
      const res = await axios.get(`${API}/admin/whitelist`, authHeader);
      setWhitelist(res.data.merchants || []);
    } catch (error) {
      console.error('Error fetching whitelist:', error);
    }
  };

  const handleVerifyWhitelist = async (e) => {
    e.preventDefault();
    if (!wlForm.alias && !wlForm.store_id) {
      toast.error('Isi alias atau store_id');
      return;
    }
    if (!wlForm.lon || !wlForm.lat) {
      toast.error('Isi lokasi (lon & lat) toko');
      return;
    }
    setWlVerifying(true);
    setWlVerified(null);
    try {
      const res = await axios.post(`${API}/admin/whitelist/verify`, {
        alias: wlForm.alias || null,
        store_id: wlForm.store_id ? parseInt(wlForm.store_id) : null,
        lon: parseFloat(wlForm.lon),
        lat: parseFloat(wlForm.lat),
      }, authHeader);

      if (res.data.found) {
        setWlVerified(res.data.merchant);
        toast.success('Merchant ditemukan!');
      } else {
        toast.error(res.data.message || 'Merchant tidak ditemukan');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Gagal verifikasi merchant');
    } finally {
      setWlVerifying(false);
    }
  };

  const handleAddWhitelist = async () => {
    if (!wlVerified) return;
    setWlAdding(true);
    try {
      await axios.post(`${API}/admin/whitelist`, {
        store_id: wlVerified.store_id,
        alias: wlVerified.alias || '',
        name: wlVerified.name || '',
        lon: wlVerified.lon,
        lat: wlVerified.lat,
      }, authHeader);
      toast.success('Merchant ditambahkan ke whitelist!');
      setWlVerified(null);
      setWlForm({ alias: '', store_id: '', lon: '', lat: '' });
      fetchWhitelist();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Gagal menambahkan merchant');
    } finally {
      setWlAdding(false);
    }
  };

  const handleToggleWhitelist = async (m) => {
    try {
      await axios.put(`${API}/admin/whitelist/${m.store_id}?is_active=${!m.is_active}`,
        null, authHeader);
      fetchWhitelist();
      toast.success(`Merchant ${!m.is_active ? 'diaktifkan' : 'dinonaktifkan'}`);
    } catch (error) {
      toast.error('Gagal update merchant');
    }
  };

  const handleDeleteWhitelist = async (storeId) => {
    if (!confirm('Hapus merchant ini dari whitelist?')) return;
    try {
      await axios.delete(`${API}/admin/whitelist/${storeId}`, authHeader);
      toast.success('Merchant dihapus dari whitelist');
      fetchWhitelist();
    } catch (error) {
      toast.error('Gagal menghapus merchant');
    }
  };

  // Login Screen
  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-[#F8F9FA] flex items-center justify-center p-4" data-testid="admin-login">
        <div className="w-full max-w-md">
          <div className="bg-white rounded-2xl shadow-lg p-8">
            <div className="text-center mb-8">
              <div className="w-16 h-16 bg-[#0052CC] rounded-xl flex items-center justify-center mx-auto mb-4">
                <span className="text-white font-bold text-2xl">M</span>
              </div>
              <h1 className="font-heading text-2xl font-bold text-gray-900">Admin Dashboard</h1>
              <p className="text-gray-500 mt-2">GIZI.AI</p>
            </div>

            <form onSubmit={handleLogin} className="space-y-4">
              <div>
                <Label htmlFor="password">Password Admin</Label>
                <Input
                  id="password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Masukkan password"
                  className="mt-1"
                  data-testid="admin-password-input"
                />
              </div>
              <Button
                type="submit"
                disabled={loading}
                className="w-full bg-[#0052CC] hover:bg-[#0043A8]"
                data-testid="admin-login-btn"
              >
                {loading ? 'Loading...' : 'Login'}
              </Button>
            </form>

            <div className="mt-6 text-center">
              <Link to="/" className="text-sm text-[#0052CC] hover:underline">
                ← Kembali ke Marketplace
              </Link>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Dashboard
  return (
    <div className="min-h-screen bg-[#F8F9FA]" data-testid="admin-dashboard">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-white border-b border-gray-100 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Link to="/" className="text-gray-600 hover:text-gray-900">
              <ArrowLeft className="w-5 h-5" />
            </Link>
            <h1 className="font-heading text-xl font-bold text-gray-900">
              Admin Dashboard
            </h1>
          </div>
          <Button variant="outline" onClick={handleLogout} data-testid="admin-logout-btn">
            <LogOut className="w-4 h-4 mr-2" />
            Logout
          </Button>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 py-8">
        {/* Tabs */}
        <div className="flex gap-2 mb-6">
          <Button
            variant={activeTab === 'banners' ? 'default' : 'outline'}
            onClick={() => setActiveTab('banners')}
            className={activeTab === 'banners' ? 'bg-[#0052CC]' : ''}
            data-testid="tab-banners"
          >
            <Image className="w-4 h-4 mr-2" />
            Banner
          </Button>
          <Button
            variant={activeTab === 'orders' ? 'default' : 'outline'}
            onClick={() => setActiveTab('orders')}
            className={activeTab === 'orders' ? 'bg-[#0052CC]' : ''}
            data-testid="tab-orders"
          >
            <ShoppingCart className="w-4 h-4 mr-2" />
            Pesanan ({orders.length})
          </Button>
          <Button
            variant={activeTab === 'tutorial' ? 'default' : 'outline'}
            onClick={() => setActiveTab('tutorial')}
            className={activeTab === 'tutorial' ? 'bg-[#0052CC]' : ''}
            data-testid="tab-tutorial"
          >
            <BookOpen className="w-4 h-4 mr-2" />
            Tutorial Seller
          </Button>
          <Button
            variant={activeTab === 'whitelist' ? 'default' : 'outline'}
            onClick={() => setActiveTab('whitelist')}
            className={activeTab === 'whitelist' ? 'bg-[#0052CC]' : ''}
            data-testid="tab-whitelist"
          >
            <Store className="w-4 h-4 mr-2" />
            Whitelist ({whitelist.length})
          </Button>
        </div>

        {/* Banner Management */}
        {activeTab === 'banners' && (
          <div className="space-y-6">
            {/* Upload Form */}
            <div className="bg-white rounded-xl border border-gray-100 p-6" data-testid="banner-upload-form">
              <h2 className="font-heading text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
                <Upload className="w-5 h-5 text-[#0052CC]" />
                Upload Banner Baru
              </h2>

              <form onSubmit={handleUploadBanner} className="space-y-4">
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="title">Judul Banner *</Label>
                    <Input
                      id="title"
                      value={uploadForm.title}
                      onChange={(e) => setUploadForm(prev => ({ ...prev, title: e.target.value }))}
                      placeholder="Promo Spesial Hari Ini"
                      className="mt-1"
                      data-testid="banner-title-input"
                    />
                  </div>
                  <div>
                    <Label htmlFor="link">Link URL (opsional)</Label>
                    <Input
                      id="link"
                      value={uploadForm.link_url}
                      onChange={(e) => setUploadForm(prev => ({ ...prev, link_url: e.target.value }))}
                      placeholder="https://example.com/promo"
                      className="mt-1"
                      data-testid="banner-link-input"
                    />
                  </div>
                </div>

                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="file">Gambar Banner *</Label>
                    <Input
                      id="file"
                      type="file"
                      accept="image/*"
                      onChange={handleFileChange}
                      className="mt-1"
                      data-testid="banner-file-input"
                    />
                    <p className="text-xs text-gray-500 mt-1">Rekomendasi: 1200x400 pixels</p>
                  </div>
                  <div>
                    <Label htmlFor="order">Urutan</Label>
                    <Input
                      id="order"
                      type="number"
                      value={uploadForm.order}
                      onChange={(e) => setUploadForm(prev => ({ ...prev, order: parseInt(e.target.value) || 0 }))}
                      className="mt-1"
                      data-testid="banner-order-input"
                    />
                  </div>
                </div>

                {uploadForm.file && (
                  <div className="bg-gray-50 rounded-lg p-4">
                    <p className="text-sm text-gray-600">Preview:</p>
                    <img
                      src={URL.createObjectURL(uploadForm.file)}
                      alt="Preview"
                      className="mt-2 max-h-40 rounded-lg object-cover"
                    />
                  </div>
                )}

                <Button
                  type="submit"
                  disabled={uploading}
                  className="bg-[#0052CC] hover:bg-[#0043A8]"
                  data-testid="banner-upload-btn"
                >
                  {uploading ? 'Uploading...' : 'Upload Banner'}
                </Button>
              </form>
            </div>

            {/* Banner List */}
            <div className="bg-white rounded-xl border border-gray-100 p-6" data-testid="banner-list">
              <h2 className="font-heading text-lg font-bold text-gray-900 mb-4">
                Daftar Banner ({banners.length})
              </h2>

              {banners.length === 0 ? (
                <div className="text-center py-12 text-gray-500">
                  <Image className="w-12 h-12 mx-auto mb-4 text-gray-300" />
                  <p>Belum ada banner</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {banners.map((banner) => (
                    <div
                      key={banner.id}
                      className="flex items-center gap-4 p-4 bg-gray-50 rounded-xl"
                      data-testid={`banner-item-${banner.id}`}
                    >
                      <GripVertical className="w-5 h-5 text-gray-400 cursor-grab" />

                      <img
                        src={banner.image_url}
                        alt={banner.title}
                        className="w-32 h-20 object-cover rounded-lg"
                      />

                      <div className="flex-1 min-w-0">
                        {editingBanner?.id === banner.id ? (
                          <div className="space-y-3">
                            {/* Title */}
                            <div>
                              <Label className="text-xs">Judul</Label>
                              <Input
                                value={editingBanner.title}
                                onChange={(e) => setEditingBanner(prev => ({ ...prev, title: e.target.value }))}
                                placeholder="Judul banner"
                                className="text-sm"
                              />
                            </div>

                            {/* Link URL */}
                            <div>
                              <Label className="text-xs">Link URL</Label>
                              <Input
                                value={editingBanner.link_url || ''}
                                onChange={(e) => setEditingBanner(prev => ({ ...prev, link_url: e.target.value }))}
                                placeholder="https://example.com"
                                className="text-sm"
                              />
                            </div>

                            {/* Order */}
                            <div>
                              <Label className="text-xs">Urutan</Label>
                              <Input
                                type="number"
                                value={editingBanner.order}
                                onChange={(e) => setEditingBanner(prev => ({ ...prev, order: parseInt(e.target.value) || 0 }))}
                                className="text-sm"
                              />
                            </div>

                            {/* Image Upload */}
                            <div>
                              <Label className="text-xs">Ganti Gambar (opsional)</Label>
                              <Input
                                type="file"
                                accept="image/*"
                                onChange={(e) => setEditImageFile(e.target.files[0])}
                                className="text-sm"
                              />
                              {editImageFile && (
                                <div className="mt-2 relative">
                                  <img
                                    src={URL.createObjectURL(editImageFile)}
                                    alt="Preview"
                                    className="w-full h-32 object-cover rounded-lg"
                                  />
                                  <button
                                    onClick={() => setEditImageFile(null)}
                                    className="absolute top-2 right-2 p-1 bg-red-500 text-white rounded-full hover:bg-red-600"
                                  >
                                    <X className="w-3 h-3" />
                                  </button>
                                </div>
                              )}
                            </div>
                          </div>
                        ) : (
                          <>
                            <h3 className="font-medium text-gray-900 truncate">{banner.title}</h3>
                            {banner.link_url && (
                              <p className="text-sm text-gray-500 truncate">{banner.link_url}</p>
                            )}
                            <p className="text-xs text-gray-400">Urutan: {banner.order}</p>
                          </>
                        )}
                      </div>

                      <div className="flex items-center gap-2">
                        {/* Active Toggle */}
                        <button
                          onClick={() => handleToggleBanner(banner)}
                          className={`p-2 rounded-lg transition-colors ${banner.is_active
                            ? 'bg-green-100 text-green-600'
                            : 'bg-gray-200 text-gray-400'
                            }`}
                          title={banner.is_active ? 'Aktif' : 'Nonaktif'}
                        >
                          {banner.is_active ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
                        </button>

                        {/* Edit/Save */}
                        {editingBanner?.id === banner.id ? (
                          <>
                            <button
                              onClick={() => handleUpdateBanner(banner)}
                              className="p-2 bg-green-100 text-green-600 rounded-lg hover:bg-green-200"
                            >
                              <Save className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => setEditingBanner(null)}

                              className="p-2 bg-gray-100 text-gray-600 rounded-lg hover:bg-gray-200"
                            >
                              <X className="w-4 h-4" />
                            </button>
                          </>
                        ) : (
                          <button
                            onClick={() => setEditingBanner({ ...banner })}
                            className="p-2 bg-blue-100 text-blue-600 rounded-lg hover:bg-blue-200"
                          >
                            <Edit2 className="w-4 h-4" />
                          </button>
                        )}

                        {/* Delete */}
                        <button
                          onClick={() => handleDeleteBanner(banner.id)}
                          className="p-2 bg-red-100 text-red-600 rounded-lg hover:bg-red-200"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Orders List */}
        {activeTab === 'orders' && (
          <div className="bg-white rounded-xl border border-gray-100 p-6" data-testid="orders-list">
            <h2 className="font-heading text-lg font-bold text-gray-900 mb-4">
              Daftar Pesanan ({orders.length})
            </h2>

            {orders.length === 0 ? (
              <div className="text-center py-12 text-gray-500">
                <ShoppingCart className="w-12 h-12 mx-auto mb-4 text-gray-300" />
                <p>Belum ada pesanan</p>
              </div>
            ) : (
              <div className="space-y-4">
                {orders.map((order) => (
                  <div
                    key={order.id}
                    className="p-4 bg-gray-50 rounded-xl"
                    data-testid={`order-item-${order.id}`}
                  >
                    <div className="flex items-start justify-between mb-3">
                      <div>
                        <p className="font-mono text-sm font-bold text-gray-900">{order.order_number}</p>
                        <p className="text-xs text-gray-500">{formatDate(order.created_at)}</p>
                      </div>
                      <span className={`px-3 py-1 rounded-full text-xs font-semibold ${order.status === 'pending'
                        ? 'bg-yellow-100 text-yellow-700'
                        : order.status === 'completed'
                          ? 'bg-green-100 text-green-700'
                          : 'bg-gray-100 text-gray-700'
                        }`}>
                        {order.status}
                      </span>
                    </div>

                    <div className="grid md:grid-cols-2 gap-4 text-sm">
                      <div>
                        <p className="text-gray-500">Penerima:</p>
                        <p className="font-medium">{order.address.name}</p>
                        <p>{order.address.phone}</p>
                        <p className="text-gray-600">{order.address.address}</p>
                        <p className="text-gray-600">{order.address.district}, {order.address.city}</p>
                      </div>
                      <div>
                        <p className="text-gray-500">Pembayaran:</p>
                        <p className="font-medium capitalize">{order.payment.type.replace('_', ' ')}</p>
                        {order.payment.bank_code && (
                          <p className="text-gray-600 uppercase">{order.payment.bank_code}</p>
                        )}
                        <p className="text-lg font-bold text-[#0052CC] mt-2">
                          Rp{formatPrice(order.total_amount)}
                        </p>
                      </div>
                    </div>

                    <div className="mt-3 pt-3 border-t border-gray-200">
                      <p className="text-gray-500 text-xs mb-2">Items ({order.items.length}):</p>
                      <div className="flex flex-wrap gap-2">
                        {order.items.map((item, idx) => (
                          <span key={idx} className="text-xs bg-white px-2 py-1 rounded border">
                            {item.product_name} x{item.quantity}
                          </span>
                        ))}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Tutorial Seller Management */}
        {activeTab === 'tutorial' && (
          <div className="space-y-6">
            {/* Header with Init button */}
            <div className="bg-white rounded-xl border border-gray-100 p-6" data-testid="tutorial-management">
              <div className="flex items-center justify-between mb-4">
                <h2 className="font-heading text-lg font-bold text-gray-900 flex items-center gap-2">
                  <BookOpen className="w-5 h-5 text-[#0052CC]" />
                  Tutorial Seller ({tutorialSteps.length} langkah)
                </h2>
                <Button
                  onClick={handleInitTutorial}
                  disabled={initializingTutorial}
                  variant="outline"
                  size="sm"
                  data-testid="init-tutorial-btn"
                >
                  {initializingTutorial ? (
                    <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Inisialisasi...</>
                  ) : (
                    <><Upload className="w-4 h-4 mr-2" />Init / Reset Tutorial</>
                  )}
                </Button>
              </div>
              <p className="text-sm text-gray-500">
                Kelola konten tutorial langkah-langkah berjualan. Klik "Init / Reset Tutorial" untuk membuat atau mereset tabel di database.
              </p>
            </div>

            {/* Steps list */}
            {tutorialSteps.length === 0 ? (
              <div className="bg-white rounded-xl border border-gray-100 p-12 text-center">
                <BookOpen className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                <p className="text-gray-500 mb-4">Belum ada data tutorial.</p>
                <Button
                  onClick={handleInitTutorial}
                  disabled={initializingTutorial}
                  className="bg-[#0052CC] hover:bg-[#0043A8]"
                >
                  Inisialisasi Tutorial
                </Button>
              </div>
            ) : (
              <div className="space-y-4">
                {tutorialSteps.map((step) => (
                  <div
                    key={step.id}
                    className="bg-white rounded-xl border border-gray-100 p-6"
                    data-testid={`tutorial-step-${step.id}`}
                  >
                    {editingStep?.id === step.id ? (
                      /* Edit Mode */
                      <div className="space-y-4">
                        <div className="flex items-center gap-2 mb-2">
                          <span className="w-8 h-8 bg-[#0052CC] text-white rounded-lg flex items-center justify-center font-bold text-sm">
                            {step.step_number}
                          </span>
                          <span className="text-sm font-semibold text-gray-500">Editing Step {step.step_number}</span>
                        </div>

                        <div>
                          <Label className="text-xs">Judul</Label>
                          <Input
                            value={editingStep.title}
                            onChange={(e) => setEditingStep(prev => ({ ...prev, title: e.target.value }))}
                            className="mt-1"
                          />
                        </div>

                        <div>
                          <Label className="text-xs">Deskripsi singkat</Label>
                          <Input
                            value={editingStep.description}
                            onChange={(e) => setEditingStep(prev => ({ ...prev, description: e.target.value }))}
                            className="mt-1"
                          />
                        </div>

                        <div>
                          <Label className="text-xs">Instruksi Detail (pisahkan per baris)</Label>
                          <textarea
                            value={editingStep.detail_instructions || ''}
                            onChange={(e) => setEditingStep(prev => ({ ...prev, detail_instructions: e.target.value }))}
                            rows={6}
                            className="mt-1 w-full rounded-md border border-gray-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#0052CC]/20 focus:border-[#0052CC]"
                            placeholder="Langkah 1...\nLangkah 2...\nLangkah 3..."
                          />
                        </div>

                        <div>
                          <Label className="text-xs">Upload Gambar Mockup (opsional)</Label>
                          <Input
                            type="file"
                            accept="image/*"
                            onChange={(e) => setStepImageFile(e.target.files[0])}
                            className="mt-1"
                          />
                          {stepImageFile && (
                            <div className="mt-2 relative inline-block">
                              <img
                                src={URL.createObjectURL(stepImageFile)}
                                alt="Preview"
                                className="h-32 rounded-lg object-cover"
                              />
                              <button
                                onClick={() => setStepImageFile(null)}
                                className="absolute top-1 right-1 p-1 bg-red-500 text-white rounded-full hover:bg-red-600"
                              >
                                <X className="w-3 h-3" />
                              </button>
                            </div>
                          )}
                        </div>

                        <div className="flex gap-2 pt-2">
                          <Button
                            onClick={() => handleUpdateStep(step)}
                            disabled={updatingStep}
                            className="bg-[#0052CC] hover:bg-[#0043A8]"
                            size="sm"
                          >
                            {updatingStep ? (
                              <><Loader2 className="w-4 h-4 mr-1 animate-spin" />Saving...</>
                            ) : (
                              <><Save className="w-4 h-4 mr-1" />Simpan</>
                            )}
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => { setEditingStep(null); setStepImageFile(null); }}
                          >
                            <X className="w-4 h-4 mr-1" />Batal
                          </Button>
                        </div>
                      </div>
                    ) : (
                      /* View Mode */
                      <div className="flex items-start gap-4">
                        <span className="flex-shrink-0 w-10 h-10 bg-[#0052CC]/10 text-[#0052CC] rounded-xl flex items-center justify-center font-bold">
                          {step.step_number}
                        </span>

                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <h3 className="font-semibold text-gray-900">{step.title}</h3>
                            {!step.is_active && (
                              <span className="text-xs bg-gray-100 text-gray-500 px-2 py-0.5 rounded-full">Nonaktif</span>
                            )}
                          </div>
                          <p className="text-sm text-gray-500 mt-0.5">{step.description}</p>

                          {step.image_url && (
                            <div className="mt-3">
                              <img
                                src={step.image_url}
                                alt={step.title}
                                className="h-24 rounded-lg object-cover border"
                              />
                            </div>
                          )}

                          {step.detail_instructions && (
                            <div className="mt-2 text-xs text-gray-400">
                              {step.detail_instructions.split('\n').length} instruksi
                            </div>
                          )}
                        </div>

                        <div className="flex items-center gap-2 flex-shrink-0">
                          <button
                            onClick={() => setEditingStep({ ...step })}
                            className="p-2 bg-blue-100 text-blue-600 rounded-lg hover:bg-blue-200"
                          >
                            <Edit2 className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Whitelist Merchant Management */}
        {activeTab === 'whitelist' && (
          <div className="space-y-6">
            {/* Info */}
            <div className="bg-blue-50 border border-blue-100 rounded-xl p-4 text-sm text-blue-800">
              Merchant di whitelist akan <b>selalu tampil di beranda</b> (dipin ke atas)
              untuk semua lokasi user, dan <b>semua produknya lolos</b> tanpa filter nutrisi.
              Isi alias/store_id + lokasi toko, lalu verifikasi sebelum menambah.
            </div>

            {/* Verify + Add Form */}
            <div className="bg-white rounded-xl border border-gray-100 p-6" data-testid="whitelist-form">
              <h2 className="font-heading text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
                <Search className="w-5 h-5 text-[#0052CC]" />
                Cari & Verifikasi Merchant
              </h2>

              <form onSubmit={handleVerifyWhitelist} className="space-y-4">
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <Label className="text-xs">Alias / slug toko</Label>
                    <Input
                      value={wlForm.alias}
                      onChange={(e) => setWlForm(p => ({ ...p, alias: e.target.value }))}
                      placeholder="toko-sehat-saya"
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <Label className="text-xs">Store ID (opsional jika ada alias)</Label>
                    <Input
                      type="number"
                      value={wlForm.store_id}
                      onChange={(e) => setWlForm(p => ({ ...p, store_id: e.target.value }))}
                      placeholder="12345"
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <Label className="text-xs">Longitude toko</Label>
                    <Input
                      type="number" step="any"
                      value={wlForm.lon}
                      onChange={(e) => setWlForm(p => ({ ...p, lon: e.target.value }))}
                      placeholder="106.8456"
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <Label className="text-xs">Latitude toko</Label>
                    <Input
                      type="number" step="any"
                      value={wlForm.lat}
                      onChange={(e) => setWlForm(p => ({ ...p, lat: e.target.value }))}
                      placeholder="-6.2088"
                      className="mt-1"
                    />
                  </div>
                </div>

                <Button
                  type="submit"
                  disabled={wlVerifying}
                  className="bg-[#0052CC] hover:bg-[#0043A8]"
                >
                  {wlVerifying ? (
                    <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Mencari...</>
                  ) : (
                    <><Search className="w-4 h-4 mr-2" />Cari & Verifikasi</>
                  )}
                </Button>
              </form>

              {/* Verified result */}
              {wlVerified && (
                <div className="mt-4 p-4 bg-green-50 border border-green-200 rounded-xl flex items-center justify-between gap-4">
                  <div className="flex items-start gap-3 min-w-0">
                    <CheckCircle2 className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                    <div className="min-w-0">
                      <p className="font-semibold text-gray-900 truncate">
                        {wlVerified.name || wlVerified.alias || `Store #${wlVerified.store_id}`}
                      </p>
                      <p className="text-xs text-gray-500">
                        ID: {wlVerified.store_id} · alias: {wlVerified.alias || '-'} · {wlVerified.total_product} produk
                      </p>
                    </div>
                  </div>
                  <Button
                    onClick={handleAddWhitelist}
                    disabled={wlAdding}
                    className="bg-green-600 hover:bg-green-700 flex-shrink-0"
                    size="sm"
                  >
                    {wlAdding ? (
                      <><Loader2 className="w-4 h-4 mr-1 animate-spin" />...</>
                    ) : (
                      <><Plus className="w-4 h-4 mr-1" />Tambah ke Whitelist</>
                    )}
                  </Button>
                </div>
              )}
            </div>

            {/* Whitelist List */}
            <div className="bg-white rounded-xl border border-gray-100 p-6" data-testid="whitelist-list">
              <h2 className="font-heading text-lg font-bold text-gray-900 mb-4">
                Merchant Whitelist ({whitelist.length})
              </h2>

              {whitelist.length === 0 ? (
                <div className="text-center py-12 text-gray-500">
                  <Store className="w-12 h-12 mx-auto mb-4 text-gray-300" />
                  <p>Belum ada merchant di whitelist</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {whitelist.map((m) => (
                    <div
                      key={m.store_id}
                      className="flex items-center gap-4 p-4 bg-gray-50 rounded-xl"
                      data-testid={`whitelist-item-${m.store_id}`}
                    >
                      <div className="w-10 h-10 bg-[#0052CC]/10 text-[#0052CC] rounded-xl flex items-center justify-center flex-shrink-0">
                        <Store className="w-5 h-5" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <h3 className="font-medium text-gray-900 truncate">
                          {m.name || m.alias || `Store #${m.store_id}`}
                        </h3>
                        <p className="text-xs text-gray-500 truncate">
                          ID: {m.store_id} · alias: {m.alias || '-'} · ({m.lon}, {m.lat})
                        </p>
                      </div>
                      <div className="flex items-center gap-2 flex-shrink-0">
                        <button
                          onClick={() => handleToggleWhitelist(m)}
                          className={`p-2 rounded-lg transition-colors ${m.is_active
                            ? 'bg-green-100 text-green-600'
                            : 'bg-gray-200 text-gray-400'}`}
                          title={m.is_active ? 'Aktif' : 'Nonaktif'}
                        >
                          {m.is_active ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
                        </button>
                        <button
                          onClick={() => handleDeleteWhitelist(m.store_id)}
                          className="p-2 bg-red-100 text-red-600 rounded-lg hover:bg-red-200"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}
      </main>
    </div>
  );
}
